// A row that stands for SEVERAL limits lists each limit on its own line inside the row:
// one line per limit in every value column, and one tick per limit in the tick column,
// each on its own limit's line. Owner of the stacked-limit layout (Aaron 2026-10-06 on
// the aircraft Inspections tab; 2026-10-08 the Components tab, as legacy's tab stacks a
// component's limits: mod_aircraft/ajax/aircraft_components_tab.inc:793-1175).
//
//   ☐ 70BM035420  MODULE 3   Engine1 Hours   3600   200 Hours  …   ← line 1, tick 1
//   ☐                        Date (Days)     5475   180 Days   …   ← line 2, tick 2
//
// The grid gives such a row getRowHeight "auto"; dataGridSx's
// detailRowControlsOnFirstLineSx keeps the reveal chevron on the first line.
// Modules supply the limits and what a line shows; the geometry lives here.
import React from "react";
import { Box } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";

import { DENSITY_ROW_HEIGHT } from "../../utils/dataGridSx";

/**
 * Every limit's line is ONE GRID ROW (Aaron 2026-10-12: "the lines between the rows should match
 * and the spacing should be even"). A compact MUI X 7 row is 36px: a 1px top border in
 * `--DataGrid-rowBorderColor` and 35px of content. So the first line is the row's own 35px (its
 * cell already draws the row's top border), and every later line is 36px: a 1px top border plus
 * the same 35px. A stacked row of n limits is exactly n rows tall, each line's text centres where a
 * single row's does, and a divided stack's rule sits exactly where a row border would.
 * Values and ticks share this geometry, so every tick sits on its own limit's line.
 */
export const STACK_ROW_PX = DENSITY_ROW_HEIGHT.Compact;
/**
 * The row rhythm FOLLOWS THE GRID'S DENSITY (Aaron 2026-10-12, "the spacing should be even"): compact
 * (the default, STACK_ROW_PX) and, under the grid root's density class, standard and comfortable —
 * a line is always that density's row, so a stack of n limits is n rows at any density.
 */
function lineRhythm(rowPx: number) {
  return {
    "& > *": { height: `${rowPx}px !important`, lineHeight: `${rowPx - 1}px !important` },
    "& > *:first-child": { height: `${rowPx - 1}px !important` },
  };
}
const DENSITY_RHYTHM = Object.fromEntries(
  (["Standard", "Comfortable"] as const).map((d) => [`.MuiDataGrid-root--density${d} &`, lineRhythm(DENSITY_ROW_HEIGHT[d])])
);

/** One line's content height (a compact row less its 1px border): its text's line box. */
export const STACK_LINE_PX = STACK_ROW_PX - 1;
/** No inset above the first line: it is the row's own content box. */
export const STACK_TOP_PX = 0;
/** The grid's own row border colour (MUI X 7 draws every cell's 1px solid top border in it). */
export const GRID_ROW_BORDER_COLOR = "var(--DataGrid-rowBorderColor)";
/** The grid's own row border, as a whole. */
export const GRID_ROW_BORDER = `1px solid ${GRID_ROW_BORDER_COLOR}`;

/** A grid cell's own side padding (MUI X 7: `0 10px`); a divided stack bleeds across it. */
const CELL_PAD_PX = 10;

/**
 * The column of lines in one cell.
 *
 * `divided` (Aaron 2026-10-12: "need the line between the limitations for the non row span
 * rows", "the line between limitations should apply to all tabs"; then "the lines between the
 * rows should match"): the grid's OWN row border (GRID_ROW_BORDER: same colour, 1px, solid)
 * between one limit's line and the next, across the cell's full width. For the PER-LIMIT columns
 * only — the row-spanning ones (tick, Part Number, Serial, the ATA heading) stay undivided. An
 * undivided stack draws the same 1px border transparent, so every column keeps the same line
 * heights and every tick stays on its own limit's line.
 * `divided="inline"`: the same rule within the stack's own width, for a stack that shares its
 * cell with something else (Components' per-limit [LT: …] labels beside the description).
 */
export const StackedLines: React.FC<{
  testId?: string;
  fullWidth?: boolean;
  divided?: boolean | "inline";
  children?: React.ReactNode;
}> = ({ testId, fullWidth, divided, children }) => {
  const bleed = divided === true;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        pt: `${STACK_TOP_PX}px`,
        alignSelf: "flex-start",
        width: bleed ? `calc(100% + ${2 * CELL_PAD_PX}px)` : fullWidth ? "100%" : undefined,
        // the row rhythm, owned here for ANY line a module renders (StackedLine or its own):
        // later lines are a row (border + content), the first is the row's own content box
        "& > *": {
          boxSizing: "border-box",
          height: `${STACK_ROW_PX}px !important`,
          lineHeight: `${STACK_LINE_PX}px !important`,
          borderTopWidth: "1px",
          borderTopStyle: "solid",
          borderTopColor: "transparent",
          ...(bleed ? { px: `${CELL_PAD_PX}px` } : {}),
        },
        "& > *:first-child": { height: `${STACK_LINE_PX}px !important`, borderTopWidth: 0, borderTopStyle: "none" },
        // standard / comfortable grids: the same rhythm at their row height
        ...DENSITY_RHYTHM,
        ...(bleed ? { mx: `-${CELL_PAD_PX}px` } : {}),
        // the divider: the same 1px solid border every line already has, in the grid's row colour
        ...(divided ? { "& > * + *": { borderTopColor: GRID_ROW_BORDER_COLOR } } : {}),
      }}
      data-testid={testId}
      data-divided={divided ? String(divided) : undefined}
    >
      {children}
    </Box>
  );
};

/**
 * The class on a line that is NOT the one the row's colour is for (Inspections' LimitLines,
 * Aaron 2026-10-07). A tab's whole-row colour rule skips it (`:not(.limit-line--other)` on
 * Inspections; a later `.limit-line--other` rule on Components), so the governing line --
 * the one that comes due first -- is the only line in the row's colour; the others are grey.
 */
export const STACKED_LINE_OTHER_CLASS = "limit-line--other";

/**
 * How one line reads against its row's colour (the due status, per line, as on Inspections):
 *   undefined  the row's colour (the governing line; every line when nothing governs)
 *   "other"    grey: a line that does not govern the row
 *   "overdue"  red, whatever governs: the line's own limit is overdue
 */
export type StackedLineTone = "other" | "overdue";

const TONE_SX: Record<StackedLineTone, object> = {
  other: { color: "text.secondary" },
  overdue: { color: "error.main" },
};

/** One limit's line. `limitationId` names the limit the line is for (data-limitation-id). */
export const StackedLine: React.FC<{
  limitationId?: number | string;
  tone?: StackedLineTone;
  children?: React.ReactNode;
}> = ({ limitationId, tone, children }) => (
  <Box
    data-limitation-id={limitationId}
    data-line-tone={tone}
    className={tone === "other" ? STACKED_LINE_OTHER_CLASS : undefined}
    sx={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", ...(tone ? TONE_SX[tone] : {}) }}
  >
    {children}
  </Box>
);

/** A column's own value for one limit: its valueGetter (v7: (value, row)) on that limit, else the field. */
export function lineValue(c: GridColDef, lim: any): unknown {
  return c.valueGetter ? (c.valueGetter as any)(undefined, lim, c, undefined) : lim?.[c.field];
}

/**
 * Set on a stacked column: the limits a row lists, one per line. The grid's CSV
 * (gridCsvExport) reads it to write one line per limit, in this order.
 */
export const STACKED_LIMITS_OF = "stackedLimitsOf" as const;

/**
 * Column `c`, stacked: a row whose `limitsOf` lists 2+ limits renders `renderLines` (one
 * StackedLine per limit); any other row renders the column as it was. The cell's value
 * (sort, filter) stays the row's own; the CSV writes one line per limit, each with that
 * limit's value as the column formats it (`valueFormatter`), in `limitsOf`'s order.
 */
export function stackedColumn<R = any, L = any>(
  c: GridColDef,
  limitsOf: (row: R) => L[] | null | undefined,
  renderLines: (params: GridRenderCellParams, limits: L[]) => React.ReactNode
): GridColDef {
  const col: GridColDef = {
    ...c,
    renderCell: (params: GridRenderCellParams) => {
      const lims = params.row ? limitsOf(params.row as R) : null;
      if (!lims || lims.length < 2) {
        return c.renderCell ? c.renderCell(params) : params.formattedValue ?? params.value;
      }
      return renderLines(params, lims);
    },
  };
  return Object.assign(col, { [STACKED_LIMITS_OF]: limitsOf });
}

/**
 * The default lines: each limit's cell exactly as the column renders it for a one-limit
 * row (its renderCell on that limit, else its value), one per line, in the given order.
 * `toneOf` sets each line's tone (StackedLineTone): the due-status colour per line.
 */
export function columnLines<L extends { id?: number | string }>(
  c: GridColDef,
  params: GridRenderCellParams,
  limits: L[],
  toneOf?: (lim: L) => StackedLineTone | undefined
): React.ReactNode {
  return (
    <StackedLines testId={`limits-${c.field}-${params.row?.id}`} divided>
      {limits.map((lim, i) => {
        const value = lineValue(c, lim);
        const content = c.renderCell
          ? c.renderCell({ ...params, row: lim, value, formattedValue: value } as GridRenderCellParams)
          : value === "" || value === null || value === undefined
          ? " "
          : String(value);
        return (
          <StackedLine key={lim.id ?? i} limitationId={lim.id} tone={toneOf?.(lim)}>
            {content}
          </StackedLine>
        );
      })}
    </StackedLines>
  );
}
